import React, { useState, useEffect } from "react";
import { getData, postData, putData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaStore,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaShoppingCart,
  FaGlobe,
  FaClock
} from "react-icons/fa";

function EcommerceTypesManager() {
  const [ecommerceTypes, setEcommerceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    ecommerce_name: '',
    active: true,
    estimated_delivery_days: 15
  });

  useEffect(() => {
    loadEcommerceTypes();
  }, []);

  const loadEcommerceTypes = async () => {
    try {
      setLoading(true);
    
      const response = await getData('/api/admin/get_ecommerce_types');      
      // Inicializar como array vacío por defecto
      let typesData = [];
      
      if (response && response.data) {
        // Si response.data es directamente un array
        if (Array.isArray(response.data)) {
          typesData = response.data;
        }
        // Si está en response.data.ecommerceTypes
        else if (response.data.ecommerceTypes && Array.isArray(response.data.ecommerceTypes)) {
          typesData = response.data.ecommerceTypes;
        }
        // Si está en response.data.types
        else if (response.data.types && Array.isArray(response.data.types)) {
          typesData = response.data.types;
        }
        // Explorar otras propiedades posibles
        else {
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && typesData.length === 0) {
              typesData = response.data[key];
            }
          });
        }
      }
          
      // Asegurar que siempre sea un array
      if (!Array.isArray(typesData)) {
        typesData = [];
      }
      
      setEcommerceTypes(typesData);
    } catch (error) {
      console.error('Error cargando tipos de ecommerce:', error);
      toastError('Error al cargar los tipos de ecommerce');
      setEcommerceTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          ecommerce_name: formData.ecommerce_name.trim().toUpperCase(),
          active: formData.active ? 1 : 0,
          estimated_delivery_days: parseInt(formData.estimated_delivery_days) || 15,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingType) {
        dataToSend.data.id = editingType.id;
        await putData('/api/admin/update_ecommerce_type', dataToSend);
        toastSuccess(`Tipo "${formData.ecommerce_name}" actualizado exitosamente`);
      } else {
        await postData('/api/admin/create_ecommerce_type', dataToSend);
        toastSuccess(`Tipo "${formData.ecommerce_name}" creado exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadEcommerceTypes();
    } catch (error) {
      console.error('Error guardando tipo de ecommerce:', error);
      toastError(editingType ? 'Error al actualizar el tipo' : 'Error al crear el tipo');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      ecommerce_name: '',
      active: true,
      estimated_delivery_days: 15
    });
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      ecommerce_name: type.ecommerce_name || '',
      active: type.active !== undefined ? Boolean(type.active) : true,
      estimated_delivery_days: type.estimated_delivery_days || 15
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingType(null);
    setFormData({
      ecommerce_name: '',
      active: true,
      estimated_delivery_days: 15
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (type) => {
    setDeletingType(type);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingType) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo (soft delete)
      const dataToSend = {
        data: {
          id: deletingType.id,
          ecommerce_name: deletingType.ecommerce_name,
          estimated_delivery_days: deletingType.estimated_delivery_days || 15,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_ecommerce_type', dataToSend);
      toastSuccess(`Tipo "${deletingType.ecommerce_name}" eliminado exitosamente`);
      setShowDeleteModal(false);
      setDeletingType(null);
      await loadEcommerceTypes();
    } catch (error) {
      console.error('Error eliminando tipo:', error);
      toastError('Error al eliminar el tipo');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingType(null);
  };

  // Ejemplos de tipos de ecommerce comunes
  const typeExamples = [
    'MERCADOLIBRE',
    'TIENDA NUBE',
    'SHOPIFY',
    'MAGENTO',
    'WOOCOMMERCE',
    'PRESTASHOP',
    'FACEBOOK MARKETPLACE',
    'INSTAGRAM SHOPPING',
    'AMAZON'
  ];

  // Filtrar tipos de ecommerce según el toggle
  const filteredEcommerceTypes = showInactive 
    ? ecommerceTypes 
    : ecommerceTypes.filter(type => type.active);

  return (
    <div className="ecommerce-types-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaStore className="me-2 text-primary" />
            Gestión de Tipos de Ecommerce
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo Tipo
            </button>
          </div>
        </div>

        <div className="card-body">
          {!showInactive && ecommerceTypes.length > filteredEcommerceTypes.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{ecommerceTypes.length - filteredEcommerceTypes.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando tipos de ecommerce...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo de Ecommerce</th>
                    <th>Días Estimados</th>
                    <th>Estado</th>
                    <th>Creado por</th>
                    <th>Fecha Creación</th>
                    <th>Actualizado por</th>
                    <th>Última Actualización</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(filteredEcommerceTypes) || filteredEcommerceTypes.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        {!Array.isArray(ecommerceTypes) ? 
                          'Error cargando tipos de ecommerce' : 
                          showInactive ? 'No hay tipos de ecommerce registrados' : 'No hay tipos activos. Activa el toggle "Mostrar inactivos" para ver tipos eliminados.'
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredEcommerceTypes.map(type => (
                      <tr key={type.id}>
                        <td>#{type.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            {type.ecommerce_name === 'VENTA FISICA' ? (
                              <FaTimes className="me-2 text-muted" />
                            ) : (
                              <FaShoppingCart className="me-2 text-success" />
                            )}
                            <strong className="text-uppercase">{type.ecommerce_name}</strong>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-info">
                            <FaClock className="me-1" />
                            {type.estimated_delivery_days || 15} días hábiles
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${type.active ? 'bg-success' : 'bg-danger'}`}>
                            {type.active ? (
                              <>
                                <FaCheck className="me-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <FaTimes className="me-1" />
                                Inactivo
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            Usuario #{type.inserted_by || 'N/A'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.inserted_date ? new Date(type.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.updated_by ? `Usuario #${type.updated_by}` : 'Sin actualizar'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {type.updated_at ? new Date(type.updated_at).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(type)}
                            disabled={loading}
                            title="Editar tipo"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(type)}
                            disabled={loading || !type.active}
                            title={type.active ? "Eliminar tipo" : "Tipo ya inactivo"}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar tipo */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingType ? 'Editar Tipo de Ecommerce' : 'Nuevo Tipo de Ecommerce'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label className="form-label">Nombre del Tipo de Ecommerce *</label>
                        <input
                          type="text"
                          className="form-control text-uppercase"
                          name="ecommerce_name"
                          value={formData.ecommerce_name}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: MERCADOLIBRE, TIENDA NUBE, etc."
                          style={{ textTransform: 'uppercase' }}
                        />
                        <div className="form-text">
                          Se convertirá automáticamente a mayúsculas
                        </div>
                      </div>

                      {/* Botones de ejemplos rápidos */}
                      <div className="mb-3">
                        <label className="form-label">Tipos comunes:</label>
                        <div className="d-flex flex-wrap gap-1">
                          {typeExamples.map(example => (
                            <button
                              key={example}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setFormData(prev => ({ ...prev, ecommerce_name: example }))}
                              disabled={loading}
                            >
                              {example === 'VENTA FISICA' ? (
                                <FaTimes className="me-1" />
                              ) : (
                                <FaGlobe className="me-1" />
                              )}
                              {example}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Días de Fabricación Estimados *</label>
                        <input
                          type="number"
                          className="form-control"
                          name="estimated_delivery_days"
                          value={formData.estimated_delivery_days}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          min="1"
                          max="365"
                        />
                        <div className="form-text">
                          Días hábiles estimados de fabricación
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaCheck className="me-2 text-success" />
                            Tipo activo
                          </label>
                          <div className="form-text">
                            Los tipos inactivos no aparecerán disponibles para nuevos pedidos
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info">
                    <FaGlobe className="me-2" />
                    <strong>Nota:</strong> Los tipos de ecommerce ayudan a clasificar el origen de los pedidos y aplicar diferentes configuraciones según la plataforma.
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.ecommerce_name.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingType ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el tipo <strong>"{deletingType?.ecommerce_name}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El tipo será marcado como inactivo y no aparecerá disponible para nuevos pedidos.
                </div>
                <div className="alert alert-info">
                  <FaGlobe className="me-2" />
                  Los pedidos existentes que usen este tipo no se verán afectados.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EcommerceTypesManager;
